import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PortalBootstrapData } from './types/window';
import { __ } from '@/hub-shared/utils/i18n';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import { getConnectionsManageGate } from './gates/portalGates';
import ConnectionWizard from './ConnectionWizard';
import { fetchWithNonce } from './utils/fetchWithNonce';
import type { IntegrationApi } from './connectionTypes';
import type { PortalSearchSyncFn } from './utils/portalUrlState';
import { parsePortalSearchParams } from './utils/portalUrlState';
import { parseApiError } from './utils/parseApiError';
import {
  chooseComptExpertOffice,
  disconnectComptExpert,
  startComptExpertAuth,
} from './utils/comptExpertConnection';
import type { ComptExpertOffice } from './utils/comptExpertConnection';
import ComptExpertOfficeChooser from './components/ComptExpertOfficeChooser';

type ConnectionsPageProps = {
  bootstrap: PortalBootstrapData;
  onSessionExpired: () => void;
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

/** A JeSignExpert login that returned several offices: nothing is bound until the user picks one. */
type OfficeChoice = {
  connectionId: string;
  idRequest: string;
  offices: ComptExpertOffice[];
};

const ConnectionsPage = ({
  bootstrap,
  onSessionExpired,
  search,
  patchPortalSearch,
}: ConnectionsPageProps) => {
  const portalMode = typeof patchPortalSearch === 'function';
  const connectorParam = useMemo(
    () => parsePortalSearchParams(search ?? '').get('connector'),
    [search],
  );

  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState<string>('');
  const [connections, setConnections] = useState<IntegrationApi[]>([]);
  const [selectedApi, setSelectedApi] = useState<IntegrationApi | null>(null);
  const [quickActionError, setQuickActionError] = useState<string>('');
  const [quickActionPendingId, setQuickActionPendingId] = useState<string | null>(null);
  const [officeChoice, setOfficeChoice] = useState<OfficeChoice | null>(null);
  const oauthListenerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const comptExpertListenerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const baseUrl = bootstrap.restUrls.accountingHub;
  const connectionsManageGate = getConnectionsManageGate(bootstrap);
  const connectionActionsDisabled = !connectionsManageGate.allowed;

  const fetchConnections = useCallback(async () => {
    setLoadState('loading');
    setLoadError('');

    try {
      const response = await fetchWithNonce(`${baseUrl}integrations/list`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'X-WP-Nonce': bootstrap.nonce },
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (response.status === 403) {
        setLoadState('error');
        setLoadError(await parseApiError(response));
        return;
      }

      if (!response.ok) {
        setLoadState('error');
        setLoadError(await parseApiError(response));
        return;
      }

      const payload = (await response.json()) as IntegrationApi[];
      setConnections(Array.isArray(payload) ? payload : []);
      setLoadState('ready');
    } catch {
      setLoadState('error');
      setLoadError(__('Impossible de charger le statut des connexions.'));
    }
  }, [baseUrl, bootstrap.nonce, onSessionExpired]);

  useEffect(() => {
    if (loadState === 'idle') {
      void fetchConnections();
    }
  }, [loadState, fetchConnections]);

  useEffect(() => {
    const oauthListener = oauthListenerRef.current;
    const comptExpertListener = comptExpertListenerRef.current;
    return () => {
      if (oauthListener) {
        window.removeEventListener('message', oauthListener);
      }
      if (comptExpertListener) {
        window.removeEventListener('message', comptExpertListener);
      }
    };
  }, []);

  useEffect(() => {
    if (loadState !== 'ready') {
      return;
    }
    if (!portalMode) {
      return;
    }
    if (!connectorParam) {
      setSelectedApi(null);
      return;
    }
    const found = connections.find((c) => c.id === connectorParam) ?? null;
    if (found) {
      setSelectedApi(found);
    } else if (patchPortalSearch) {
      patchPortalSearch({ connector: null }, 'replace');
      setSelectedApi(null);
    }
  }, [connections, connectorParam, loadState, patchPortalSearch, portalMode]);

  useEffect(() => {
    if (!connectionActionsDisabled) {
      return;
    }
    setSelectedApi(null);
    if (portalMode && patchPortalSearch && connectorParam) {
      patchPortalSearch({ connector: null }, 'replace');
    }
  }, [connectionActionsDisabled, connectorParam, patchPortalSearch, portalMode]);

  const startQuickOAuthConnection = useCallback(
    async (connection: IntegrationApi) => {
      setQuickActionError('');
      setQuickActionPendingId(connection.id);

      const popup = window.open('about:blank', `${connection.restBasePath}_oauth`, 'width=600,height=800');
      if (!popup) {
        setQuickActionError(__("La fenêtre d'authentification a été bloquée."));
        setQuickActionPendingId(null);
        return;
      }

      try {
        const response = await fetchWithNonce(`${baseUrl}${connection.restBasePath}/prepare-oauth`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': bootstrap.nonce,
          },
          body: JSON.stringify({}),
        });

        if (response.status === 401) {
          popup.close();
          onSessionExpired();
          return;
        }

        if (response.status === 403) {
          popup.close();
          setQuickActionError(await parseApiError(response));
          return;
        }

        if (!response.ok) {
          popup.close();
          setQuickActionError(await parseApiError(response));
          return;
        }

        const payload = (await response.json()) as { authorize_url?: string };
        if (typeof payload.authorize_url !== 'string' || payload.authorize_url === '') {
          popup.close();
          setQuickActionError(__('Réponse serveur invalide.'));
          return;
        }

        const listener = (event: MessageEvent) => {
          const data = event.data ?? {};
          if (data.source !== 'accounting-hub-pennylane') {
            return;
          }

          window.removeEventListener('message', listener);
          oauthListenerRef.current = null;

          if (data.status === 'success') {
            void fetchConnections();
            return;
          }

          setQuickActionError(__('La connexion a échoué. Réessayez.'));
        };

        oauthListenerRef.current = listener;
        window.addEventListener('message', listener);

        popup.location.href = payload.authorize_url;
      } catch {
        popup.close();
        setQuickActionError(__('Impossible de démarrer la connexion.'));
      } finally {
        setQuickActionPendingId(null);
      }
    },
    [baseUrl, bootstrap.nonce, fetchConnections, onSessionExpired],
  );

  const startQuickComptExpertConnection = useCallback(
    async (connection: IntegrationApi) => {
      setQuickActionError('');
      setOfficeChoice(null);
      setQuickActionPendingId(connection.id);

      await startComptExpertAuth(
        {
          apiBaseUrl: baseUrl,
          restBasePath: connection.restBasePath,
          nonce: bootstrap.nonce,
          onSessionExpired,
          onSuccess: () => {
            void fetchConnections();
          },
          onError: setQuickActionError,
          onChooseOffice: ({ idRequest, offices }) => {
            setOfficeChoice({ connectionId: connection.id, idRequest, offices });
          },
        },
        {
          onMessageListenerAttached: (listener) => {
            if (comptExpertListenerRef.current) {
              window.removeEventListener('message', comptExpertListenerRef.current);
            }
            comptExpertListenerRef.current = listener;
          },
        },
      );

      setQuickActionPendingId(null);
    },
    [baseUrl, bootstrap.nonce, fetchConnections, onSessionExpired],
  );

  const linkComptExpertOffice = useCallback(
    async (connection: IntegrationApi, idRequest: string, officeId: string) => {
      setQuickActionError('');
      setQuickActionPendingId(connection.id);

      await chooseComptExpertOffice(
        {
          apiBaseUrl: baseUrl,
          restBasePath: connection.restBasePath,
          nonce: bootstrap.nonce,
          onSessionExpired,
          onSuccess: () => {
            setOfficeChoice(null);
            void fetchConnections();
          },
          onError: (message) => {
            setOfficeChoice(null);
            setQuickActionError(message);
          },
        },
        idRequest,
        officeId,
      );

      setQuickActionPendingId(null);
    },
    [baseUrl, bootstrap.nonce, fetchConnections, onSessionExpired],
  );

  const startQuickComptExpertDisconnect = useCallback(
    async (connection: IntegrationApi) => {
      setQuickActionError('');
      setQuickActionPendingId(connection.id);

      await disconnectComptExpert({
        apiBaseUrl: baseUrl,
        restBasePath: connection.restBasePath,
        nonce: bootstrap.nonce,
        onSessionExpired,
        onSuccess: () => {
          void fetchConnections();
        },
        onError: setQuickActionError,
      });

      setQuickActionPendingId(null);
    },
    [baseUrl, bootstrap.nonce, fetchConnections, onSessionExpired],
  );

  const toggleDevMockConnection = useCallback(
    async (connection: IntegrationApi) => {
      setQuickActionError('');
      setQuickActionPendingId(connection.id);

      try {
        const response = await fetchWithNonce(`${baseUrl}dev/mock-integrations/simulate-disconnected`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': bootstrap.nonce,
          },
          body: JSON.stringify({ simulate_disconnected: connection.connected }),
        });

        if (response.status === 401) {
          onSessionExpired();
          return;
        }

        if (!response.ok) {
          setQuickActionError(await parseApiError(response));
          return;
        }

        void fetchConnections();
      } catch {
        setQuickActionError(__('Impossible de mettre à jour la connexion.'));
      } finally {
        setQuickActionPendingId(null);
      }
    },
    [baseUrl, bootstrap.nonce, fetchConnections, onSessionExpired],
  );

  const openConfigure = (connection: IntegrationApi) => {
    if (connectionActionsDisabled) {
      return;
    }
    setSelectedApi(connection);
    if (portalMode && patchPortalSearch) {
      patchPortalSearch({ connector: connection.id }, 'push');
    }
  };

  const closeWizard = () => {
    setSelectedApi(null);
    if (portalMode && patchPortalSearch) {
      patchPortalSearch({ connector: null }, 'replace');
    }
  };

  if (loadState === 'idle' || loadState === 'loading') {
    return (
      <div aria-live="polite">
        <h2>{__('Connexions')}</h2>
        <p>{__('Chargement des connexions...')}</p>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div aria-live="polite">
        <h2>{__('Connexions')}</h2>
        <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
          <p>{__('Erreur : ')}{loadError}</p>
        </div>
        <button type="button" className="button" onClick={() => void fetchConnections()}>
          {__('Réessayer')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2>{__('Connexions')}</h2>
      <FeatureGateNotice decision={connectionsManageGate} />
      {quickActionError ? (
        <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
          <p>{quickActionError}</p>
        </div>
      ) : null}
      <div className="api-connections-grid">
        {connections.map((connection) => (
          <article
            key={connection.id}
            className={`container-style api-connection-card${connection.disabled ? ' api-connection-card--disabled' : ''}`}
            data-disabled={connection.disabled ? 'true' : undefined}
          >
            <img
              src={connection.logoUrl}
              alt={`${__('Logo')} ${connection.name}`}
              className="api-connection-card__logo"
            />
            <h3 className="api-connection-card__name">{connection.name}</h3>
            {connection.disabled ? (
              <span className="api-connection-card__coming-soon">{__('Arrive bientôt')}</span>
            ) : (
              <div className="api-connection-card__actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    if (connectionActionsDisabled) {
                      return;
                    }
                    if (connection.authType === 'mock') {
                      void toggleDevMockConnection(connection);
                      return;
                    }
                    if (connection.authType === 'oauth2' && !connection.connected) {
                      void startQuickOAuthConnection(connection);
                      return;
                    }

                    if (connection.authType === 'comptexpert') {
                      void startQuickComptExpertConnection(connection);
                      return;
                    }

                    openConfigure(connection);
                  }}
                  disabled={connectionActionsDisabled || quickActionPendingId === connection.id}
                  title={connectionActionsDisabled ? connectionsManageGate.message : undefined}
                >
                  {quickActionPendingId === connection.id
                    ? connection.authType === 'mock'
                      ? __('Mise à jour...')
                      : __('Connexion...')
                    : connection.authType === 'mock'
                      ? connection.connected
                        ? __('Déconnecter')
                        : __('Se connecter')
                      : connection.connected
                        ? __('Reconnecter')
                        : __('Se connecter')}
                </button>
                {connection.authType === 'comptexpert' && connection.connected ? (
                  <button
                    type="button"
                    className="btn-tertiary --small"
                    disabled={connectionActionsDisabled || quickActionPendingId === connection.id}
                    title={connectionActionsDisabled ? connectionsManageGate.message : undefined}
                    onClick={() => {
                      if (connectionActionsDisabled) {
                        return;
                      }
                      void startQuickComptExpertDisconnect(connection);
                    }}
                  >
                    {quickActionPendingId === connection.id ? __('Déconnexion...') : __('Déconnecter')}
                  </button>
                ) : null}
              </div>
            )}
            {connection.authType === 'comptexpert' && officeChoice?.connectionId === connection.id ? (
              <ComptExpertOfficeChooser
                radioName={`${connection.id}-office`}
                offices={officeChoice.offices}
                pending={quickActionPendingId === connection.id}
                disabled={connectionActionsDisabled}
                disabledTitle={connectionsManageGate.message}
                onSubmit={(officeId) => {
                  void linkComptExpertOffice(connection, officeChoice.idRequest, officeId);
                }}
                onCancel={() => setOfficeChoice(null)}
              />
            ) : null}
          </article>
        ))}
      </div>
      {selectedApi && !connectionActionsDisabled ? (
        <ConnectionWizard
          bootstrap={bootstrap}
          api={selectedApi}
          onClose={() => closeWizard()}
          onSessionExpired={onSessionExpired}
          onConnectionUpdated={() => {
            void fetchConnections();
          }}
        />
      ) : null}
    </div>
  );
};

export default ConnectionsPage;
